import { Link } from "react-router-dom";
import { MapPin, BedDouble, Users, Star, BadgeCheck } from "lucide-react";
import { fcfa } from "@/lib/api";
import FavoriteButton from "@/components/FavoriteButton";
import { communeName, cityName, typeName, daysLeft } from "@/lib/residences";
import useResidenceMeta from "@/hooks/useResidenceMeta";

export default function ResidenceCard({ r, meta: metaProp }) {
  const hookMeta = useResidenceMeta();
  const meta = metaProp || hookMeta;
  const photo = r.photos?.[0] || "/media/res-placeholder.jpg";
  return (
    <div data-testid={`residence-card-${r.slug}`} className="group bg-white rounded-2xl overflow-hidden border border-[#EFE6DB] shadow-[0_6px_24px_rgba(18,59,74,0.06)] hover:shadow-[0_16px_40px_rgba(18,59,74,0.14)] transition-shadow">
      <Link to={`/residence/${r.slug}`} className="block relative aspect-[4/3] overflow-hidden">
        <img src={photo} alt={r.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute top-3 left-3 flex gap-1.5">
          {r.is_featured && <span data-testid={`sponsored-badge-${r.slug}`} className="text-[10px] font-bold uppercase bg-[#FF5A4F] text-white px-2 py-1 rounded-full">Sponsorisé{daysLeft(r.featured_until) ? ` · ${daysLeft(r.featured_until)} j` : ""}</span>}
          {r.is_premium && <span className="text-[10px] font-bold uppercase bg-[#FFC857] text-[#123B4A] px-2 py-1 rounded-full flex items-center gap-1"><Star className="w-3 h-3 fill-current" />Premium</span>}
        </div>
        <div className="absolute top-3 right-3" onClick={(e) => e.preventDefault()}>
          <FavoriteButton type="residence" itemId={r.id} className="w-9 h-9 bg-[#123B4A]/60 backdrop-blur" />
        </div>
      </Link>
      <div className="p-4">
        <p className="text-[11px] font-bold uppercase tracking-wider text-[#38B5D8]">{typeName(meta, r.type)}</p>
        <Link to={`/residence/${r.slug}`}><h3 className="font-display font-bold text-[#123B4A] text-base leading-snug line-clamp-1 mt-0.5">{r.title}</h3></Link>
        <p className="text-xs text-[#71828A] flex items-center gap-1 mt-1 truncate"><MapPin className="w-3.5 h-3.5 shrink-0 text-[#FF5A4F]" />{[r.quartier, communeName(meta, r.commune), cityName(meta, r.city)].filter(Boolean).join(", ")}</p>
        <div className="flex items-center gap-3 text-xs text-[#71828A] mt-2">
          <span className="flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" />{r.bedrooms} ch.</span>
          <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{r.capacity} pers.</span>
          {r.rating_count > 0 && <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5 fill-[#FFC857] text-[#FFC857]" />{r.rating_avg}</span>}
        </div>
        <div className="flex items-end justify-between mt-3 gap-2">
          <p className="text-[#123B4A]"><span className="font-display font-black text-lg">{fcfa(r.price_night)}</span><span className="text-xs text-[#71828A]"> / nuit</span></p>
          <Link to={`/residence/${r.slug}`} data-testid={`residence-details-${r.slug}`} className="shrink-0 text-xs font-bold text-white bg-[#123B4A] hover:bg-[#FF5A4F] transition-colors px-3 py-2 rounded-full">Voir les détails</Link>
        </div>
      </div>
    </div>
  );
}

export const VerifiedBadge = () => (
  <span data-testid="host-verified-badge" className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full"><BadgeCheck className="w-3.5 h-3.5" />Propriétaire vérifié</span>
);
